/*
 * THE BROWSER'S STRIP AND TYPE CHECK of a `typescript` document (Parts 2 and
 * 3 of docs/hierarchical-models-step-3-plan.md).
 *
 * A model's text is TypeScript. The engine reads such a document as ECMAScript
 * and strips nothing, so before the text reaches anything that parses it as
 * code — the sandbox worker, the check before a write, the code view's
 * markers, the coverage reader — it is replaced by the same text with every
 * type blanked to a space, at the author's positions (statechart-host's
 * typescript.ts says how, and why the positions matter). The descriptor keeps
 * the author's TypeScript; only what the engine reads is stripped.
 *
 * WHERE IT RUNS. In a worker of its own, started on first use and kept:
 * `ts-blank-space` over the TypeScript compiler, about 9 MB that the page
 * must not carry and that Monaco's own TypeScript worker cannot lend, because
 * Monaco's `customWorkerPath` hook calls `importScripts`, which a module
 * worker (every `?worker` here, dev and build) refuses. The compiler is a
 * plain asset (vite-typescript-compiler.ts), fetched and evaluated by the
 * worker on its first request, never a module of the bundle
 * (typescript-global.ts says why). A session that never reads a model never
 * loads it.
 *
 * THE TYPE CHECK (Part 3) runs in the same worker, over the compiler it has
 * already loaded: statechart-host builds the program on the page, the worker
 * checks it (statechart-host's checker.ts) and answers each finding by its
 * offset, and the page places them. The lib files the compiler reads are an
 * asset too, fetched on the first check, so a session that only strips never
 * loads them. A check that cannot run is skipped, and the save goes on: the
 * relay checks the same document again before it keeps it.
 *
 * NOT IN A BROWSER (a Node test), the same `ts-blank-space` and the same
 * checker run in process, loaded on demand, so a test that strips or checks
 * needs no setup and the page never loads that chunk. Both give one text for
 * one document: statechart-host's typescript.test.ts pins it against the
 * relay's strip too.
 */
// Types only: the package is loaded on first use, so the shell carries none of it until a document is read.
import type {
  AsyncTypeScriptService,
  GroupSpec,
  StripKind,
  StripResult,
  TypeChecker,
  TypeCheckOptions,
  TypeCheckResult,
  TypeFinding,
  TypeProgram,
  TypeScriptModule,
} from '@vitesse/statechart-host';

/** What the page sends the worker: where the compiler and its lib files are, once, then each request. */
export type WorkerMessage =
  | { kind: 'load'; tsUrl: string; libUrl: string }
  | { kind: 'strip'; id: number; source: string; strip: StripKind }
  | { kind: 'check'; id: number; program: TypeProgram };
export type StripReply = { id: number; text: string } | { id: number; findings: TypeFinding[] } | { id: number; error: string };

let service: (AsyncTypeScriptService & TypeChecker) | undefined;

/** The page's service, made on first use: the worker in a browser, `ts-blank-space` and the checker in process elsewhere. */
export function defaultTypeScriptService(): AsyncTypeScriptService & TypeChecker {
  return (service ??= typeof Worker === 'undefined' ? inProcessService() : workerService());
}

/**
 * The type check of a `typescript` document as the engine reads it (`strip`),
 * for `CheckOptions.typeCheck`; undefined when the checker could not run, so
 * the check goes on without it.
 */
export async function typeCheckForEngine(
  scxml: string,
  strip: StripResult,
  options: TypeCheckOptions,
  checker: TypeChecker = defaultTypeScriptService(),
): Promise<TypeCheckResult | undefined> {
  if (strip.datamodel !== 'typescript') return undefined;
  try {
    const { typeCheckModel } = await import('@vitesse/statechart-host');
    return await typeCheckModel(scxml, strip, options, checker);
  } catch {
    return undefined;
  }
}

/** The text as the engine reads it: the strip of a `typescript` document, or the author's text for any other. */
export async function stripForEngine(scxml: string, strip: AsyncTypeScriptService = defaultTypeScriptService()): Promise<StripResult> {
  const { stripModelAsync } = await import('@vitesse/statechart-host');
  return stripModelAsync(scxml, strip);
}

/** A group's spec with every document as the engine reads it: the root's and each template's, stripped. */
export async function stripGroupForEngine(spec: GroupSpec): Promise<GroupSpec> {
  const templates: Record<string, GroupSpec['templates'][string]> = {};
  for (const [name, t] of Object.entries(spec.templates)) templates[name] = { ...t, scxml: (await stripForEngine(t.scxml)).text };
  return { ...spec, root: { ...spec.root, scxml: (await stripForEngine(spec.root.scxml)).text }, templates };
}

function workerService(): AsyncTypeScriptService & TypeChecker {
  let worker: Promise<Worker> | undefined;
  let next = 1;
  const waiting = new Map<number, { resolve: (answer: string | TypeFinding[]) => void; reject: (reason: Error) => void }>();
  const fail = (reason: Error): void => {
    for (const w of waiting.values()) w.reject(reason);
    waiting.clear();
  };
  const start = async (): Promise<Worker> => {
    const w = new Worker(new URL('./typescript.worker.ts', import.meta.url), { type: 'module', name: 'vitesse-typescript' });
    w.onmessage = (e: MessageEvent<StripReply>) => {
      const reply = e.data;
      const pending = waiting.get(reply.id);
      if (!pending) return;
      waiting.delete(reply.id);
      if ('error' in reply) pending.reject(new Error(reply.error));
      else pending.resolve('text' in reply ? reply.text : reply.findings);
    };
    w.onerror = (e) => {
      // The worker is gone, or never came: every open request fails, and the next one starts it again.
      fail(new Error(`the TypeScript worker failed: ${e.message || 'it could not be loaded'}`));
      worker = undefined;
    };
    // The compiler's URL, an asset of the build: the worker fetches and evaluates it before it answers.
    // The lib files' URL too, which the worker fetches on its first check.
    const { TS_URL, TS_LIB_URL } = await import('virtual:typescript-compiler').catch((err: unknown) => {
      // A worker with no compiler to load can answer nothing: end it, and the next request starts again.
      w.terminate();
      throw err;
    });
    w.postMessage({
      kind: 'load',
      tsUrl: new URL(TS_URL, location.href).href,
      libUrl: new URL(TS_LIB_URL, location.href).href,
    } satisfies WorkerMessage);
    return w;
  };
  const ask = async <T extends string | TypeFinding[]>(message: (id: number) => WorkerMessage): Promise<T> => {
    const w = await (worker ??= start().catch((err: unknown) => {
      worker = undefined;
      throw err;
    }));
    return new Promise<T>((resolve, reject) => {
      const id = next++;
      waiting.set(id, { resolve: resolve as (answer: string | TypeFinding[]) => void, reject });
      w.postMessage(message(id));
    });
  };
  return {
    strip: (source, strip) => ask<string>((id) => ({ kind: 'strip', id, source, strip })),
    check: (program) => ask<TypeFinding[]>((id) => ({ kind: 'check', id, program })),
  };
}

function inProcessService(): AsyncTypeScriptService & TypeChecker {
  const blank = import('./typescript-blank').then((m) => m.blankTypes);
  // The real package in Node; its lib files are read from beside it.
  const checker = Promise.all([import('typescript'), import('@vitesse/statechart-host')]).then(([m, host]) => {
    const ts = ((m as { default?: unknown }).default ?? m) as TypeScriptModule;
    const dir = ts.getDefaultLibFilePath({}).replace(/[^/\\]*$/, '');
    return host.createTypeChecker(ts, (name) => ts.sys.readFile(dir + name));
  });
  return {
    strip: async (source) => (await blank)(source),
    check: async (program) => (await checker).check(program),
  };
}
